
import Link from '@/components/compat/Link';
import { FileEdit, PenLine } from 'lucide-react';
import type { CmsArticle } from '@/lib/cms-types';
import { painelTableShellClass } from '@/components/painel/painel-styles';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

function formatDateTime(iso: string) {
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

type PainelRecentDraftsProps = {
  articles: CmsArticle[];
  max?: number;
  className?: string;
};

export function PainelRecentDrafts({ articles, max = 4, className }: PainelRecentDraftsProps) {
  const drafts = [...articles]
    .filter((a) => a.status === 'draft')
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, max);

  return (
    <section className={cn(painelTableShellClass, 'flex h-full flex-col', className)}>
      <div className="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-500/15 ring-1 ring-sky-400/25">
            <FileEdit className="h-4 w-4 text-sky-200" />
          </div>
          <div>
            <h2 className="font-poppins text-sm font-bold text-white">Rascunhos recentes</h2>
            <p className="font-inter text-[11px] text-slate-400">Continue de onde parou</p>
          </div>
        </div>
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="h-8 rounded-lg text-xs text-sky-200/80 hover:bg-white/10 hover:text-white"
        >
          <Link href="/painel/guias">Ver todos</Link>
        </Button>
      </div>

      {drafts.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <p className="font-inter text-sm text-slate-400">Sem rascunhos — crie um novo guia.</p>
          <Button asChild className="mt-4 rounded-xl font-poppins" size="sm">
            <Link href="/painel/guias/new">Novo guia</Link>
          </Button>
        </div>
      ) : (
        <ul className="divide-y divide-white/[0.06]">
          {drafts.map((a) => (
            <li key={a.id}>
              <div className="flex flex-col gap-3 px-5 py-4 transition-colors hover:bg-white/[0.03] sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="truncate font-poppins text-sm font-medium text-white">{a.title}</p>
                  <p className="mt-0.5 font-inter text-xs text-slate-400/80">
                    Editado {formatDateTime(a.updatedAt)}
                  </p>
                </div>
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className={cn(
                    'shrink-0 rounded-xl border-sky-400/35 bg-sky-500/10 font-poppins text-xs text-sky-100',
                    'hover:border-sky-400/60 hover:bg-sky-500/20'
                  )}
                >
                  <Link href={`/painel/guias/${a.id}/edit`}>
                    <PenLine className="mr-1.5 h-3.5 w-3.5" />
                    Editar
                  </Link>
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
